import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  buscarTribosConfigEvento,
  salvarTribosEvento,
  corValida,
  PALETA_TRIBO,
  COR_TRIBO_PADRAO,
} from '@/lib/tribo';

interface Linha {
  id: number;
  nome: string;
  usa: boolean;
  cor: string | null;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

// ---------------------------------------------------------------------------
// "Tribos do acampamento" (organizador): escolhe quais tribos o evento usa,
// pinta cada uma e ordena. Salva com salvar_tribos_evento(). Aparece na aba
// Equipes — ou em Ajustes quando o evento não tem equipes.
// ---------------------------------------------------------------------------
export default function TribosConfig({ eventoId }: { eventoId: number }) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [itens, setItens] = useState<Linha[]>([]);
  const [sugestao, setSugestao] = useState<string | null>(null);
  const [mostrarSugestao, setMostrarSugestao] = useState(false);
  const [corAberta, setCorAberta] = useState<number | null>(null);
  const [hex, setHex] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // A seção só faz sentido em acampamento ou em evento que já tem tribos salvas.
  const [podeMostrar, setPodeMostrar] = useState(true);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const d = await buscarTribosConfigEvento(eventoId);
        if (!vivo) return;
        setItens(d.tribos.map((t) => ({ id: t.id, nome: t.nome, usa: t.usa, cor: t.cor })));
        setSugestao(d.sugestao_evento_nome);
        setMostrarSugestao(!d.configurado && !!d.sugestao_evento_nome);

        const { data: ev } = await supabase
          .from('eventos')
          .select('categoria')
          .eq('id', eventoId)
          .maybeSingle();
        const categoria = (ev as { categoria?: string } | null)?.categoria;
        setPodeMostrar(d.configurado || categoria === 'acampamento');
      } catch (e) {
        if (vivo) setErro(mensagemAmigavel(e, 'Não foi possível carregar as tribos do evento.'));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [eventoId]);

  function alternarUsa(id: number) {
    setItens((arr) => arr.map((t) => (t.id === id ? { ...t, usa: !t.usa } : t)));
  }

  function definirCor(id: number, cor: string | null) {
    setItens((arr) => arr.map((t) => (t.id === id ? { ...t, cor } : t)));
  }

  function mover(indice: number, delta: number) {
    setItens((arr) => {
      const alvo = indice + delta;
      if (alvo < 0 || alvo >= arr.length) return arr;
      const copia = [...arr];
      const [item] = copia.splice(indice, 1);
      copia.splice(alvo, 0, item);
      return copia;
    });
  }

  function limparTudo() {
    setItens((arr) => arr.map((t) => ({ ...t, usa: false, cor: null })));
  }

  async function salvar() {
    setSalvando(true);
    try {
      const emUso = itens.filter((t) => t.usa);
      const payload = emUso.map((t, i) => ({ tribo_id: t.id, cor: t.cor, ordem: i + 1 }));
      await salvarTribosEvento(eventoId, payload);
      setToast('Tribos do acampamento salvas.');
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível salvar as tribos.'));
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className={`${CARD} mb-4`}>
        <div className="h-6 w-48 animate-pulse rounded bg-secondary-100" />
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-2xl bg-secondary-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!carregando && !podeMostrar) return null;

  return (
    <div className={`${CARD} mb-4`}>
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-primary-50 text-[22px] text-primary-600">
          <i className="ri-tent-line" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[16px] font-extrabold leading-tight text-foreground-950">
            Tribos do acampamento
          </h2>
          <p className="mt-0.5 text-[13px] text-foreground-600">
            Só quem é da equipe (ou campista depois do acampamento) responde a tribo. Quem só compra
            ingresso não vê nada.
          </p>
        </div>
      </div>

      {mostrarSugestao && sugestao && (
        <div className="mt-3 rounded-2xl bg-accent-50 px-3 py-3">
          <p className="text-[13px] text-foreground-800">
            Usar as mesmas tribos do <strong className="font-semibold">{sugestao}</strong>?
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMostrarSugestao(false)}
              className="cursor-pointer whitespace-nowrap rounded-full bg-accent-500 px-3 py-1.5 text-[13px] font-semibold text-white"
            >
              Usar essas
            </button>
            <button
              type="button"
              onClick={() => {
                limparTudo();
                setMostrarSugestao(false);
              }}
              className="cursor-pointer whitespace-nowrap px-2 py-1.5 text-[13px] font-semibold text-foreground-600"
            >
              Começar do zero
            </button>
          </div>
        </div>
      )}

      {erro && (
        <div className="mt-3 rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">
          {erro}
        </div>
      )}

      <div className="mt-2 divide-y divide-background-200/70">
        {itens.map((t, i) => {
          const cor = t.cor ?? COR_TRIBO_PADRAO;
          return (
            <div key={t.id} className="py-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={t.usa}
                  aria-label={`Usar a tribo ${t.nome}`}
                  onClick={() => alternarUsa(t.id)}
                  className={`relative h-6 w-10 shrink-0 cursor-pointer rounded-full transition-colors ${
                    t.usa ? 'bg-primary-500' : 'bg-secondary-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                      t.usa ? 'left-[18px]' : 'left-0.5'
                    }`}
                  />
                </button>

                <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-foreground-900">
                  {t.nome}
                </span>

                <button
                  type="button"
                  aria-label={`Cor da tribo ${t.nome}`}
                  onClick={() => {
                    setCorAberta((v) => (v === t.id ? null : t.id));
                    setHex(t.cor ?? '');
                  }}
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-200"
                >
                  <span
                    className="h-5 w-5 rounded-full"
                    style={{ backgroundColor: t.usa ? cor : 'transparent', border: `2px solid ${cor}` }}
                    aria-hidden
                  />
                </button>

                <button
                  type="button"
                  aria-label="Subir"
                  disabled={i === 0}
                  onClick={() => mover(i, -1)}
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 disabled:opacity-30"
                >
                  <i className="ri-arrow-up-s-line text-lg" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label="Descer"
                  disabled={i === itens.length - 1}
                  onClick={() => mover(i, 1)}
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 disabled:opacity-30"
                >
                  <i className="ri-arrow-down-s-line text-lg" aria-hidden />
                </button>
              </div>

              {corAberta === t.id && (
                <div className="mt-2 rounded-2xl bg-background-50 p-3">
                  <div className="grid grid-cols-8 gap-2">
                    {PALETA_TRIBO.map((c) => (
                      <button
                        key={c}
                        type="button"
                        aria-label={`Cor ${c}`}
                        onClick={() => definirCor(t.id, c)}
                        className={`h-7 w-7 cursor-pointer rounded-full transition-transform ${
                          corValida(t.cor) === c ? 'ring-2 ring-offset-1 ring-foreground-400' : ''
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={hex}
                      onChange={(e) => setHex(e.target.value)}
                      placeholder="#RRGGBB"
                      maxLength={7}
                      className="min-w-0 flex-1 rounded-xl border border-background-200 bg-white px-3 py-2 text-[14px] text-foreground-900 uppercase placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const v = corValida(hex);
                        if (v) definirCor(t.id, v);
                      }}
                      disabled={!corValida(hex)}
                      className="cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-3 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
                    >
                      Outra cor
                    </button>
                    {t.cor && (
                      <button
                        type="button"
                        onClick={() => definirCor(t.id, null)}
                        className="cursor-pointer whitespace-nowrap px-2 py-2 text-[13px] font-semibold text-foreground-500"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={salvar}
        disabled={salvando}
        className="aperta mt-3 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[16px] bg-primary-500 text-[15px] font-bold text-white shadow-pbtn transition-colors hover:bg-primary-600 disabled:opacity-60"
      >
        {salvando ? (
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" aria-hidden />
          </span>
        ) : (
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-save-line" aria-hidden />
          </span>
        )}
        Salvar
      </button>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl bg-foreground-950 px-4 py-3 text-center text-[14px] text-white">
          {toast}
        </div>
      )}
    </div>
  );
}